import { AxiosInstance } from 'axios';
import { AxiosError, type AxiosResponse } from 'axios';
import { injectBearerToken, refreshRetryOnTokenExpiry } from '../token.service';
import { reportError } from '@/services/monitoring';
import type { NetworkDiagnostics } from '@/services/monitoring/types';
import { NetworkConnection } from '@/common/types';
import { sanitizeUrl } from '@/utils/axios/sanitize-urls';

/**
 * Generic request client that handles HTTP operations
 * Abstracts away the underlying HTTP library implementation
 */
export class RequestClient {
    constructor(
        private _client: AxiosInstance,
        private _baseUrl: string,
    ) {
        _client.interceptors.request.use(injectBearerToken, (error: AxiosError) => Promise.reject(error));

        _client.interceptors.response.use(
            (response: AxiosResponse) => response,
            async (error: AxiosError) => {
                const status = error.response?.status;

                if (status === 401 || status === 403) {
                    return refreshRetryOnTokenExpiry(error);
                }

                if (!status || status >= 500) {
                    const networkDiagnostics = this._captureNetworkDiagnostics(error);

                    reportError(error, {
                        source: 'axios-interceptor',
                        errorType: !error.response ? 'network' : 'server-error',
                        url: error.config?.url,
                        method: error.config?.method,
                        status: status,
                        responseData: error.response?.data,
                        networkDiagnostics,
                    });
                }

                return Promise.reject(error);
            },
        );
    }

    /**
     * Captures network diagnostic information from an Axios error
     */
    private _captureNetworkDiagnostics(error: AxiosError): NetworkDiagnostics {
        const request = error.request as XMLHttpRequest | undefined;
        const connection = (navigator as { connection?: NetworkConnection }).connection;

        return {
            readyState: request?.readyState,
            status: request?.status,
            statusText: request?.statusText,
            responseURL: sanitizeUrl(request?.responseURL),
            responseType: request?.responseType,
            timeout: error.config?.timeout,
            withCredentials: error.config?.withCredentials,
            onlineStatus: navigator.onLine,
            effectiveType: connection?.effectiveType,
            downlink: connection?.downlink,
            rtt: connection?.rtt,
        };
    }

    /**
     * Builds the full endpoint URL
     * @param endpoint - The API endpoint
     * @returns The complete URL
     */
    private _buildEndpoint(endpoint: string): string {
        return `${this._baseUrl}/${endpoint}`;
    }

    /**
     * Makes a GET request to the specified endpoint
     * @param endpoint - The API endpoint to call
     * @returns Promise resolving to the response data
     */
    async get<T>(endpoint: string): Promise<T> {
        return (await this._client.get<T>(this._buildEndpoint(endpoint))).data;
    }

    /**
     * Makes a POST request to the specified endpoint
     * @param endpoint - The API endpoint to call
     * @param data - The request payload
     * @returns Promise resolving to the response data
     */
    async post<T>(endpoint: string, data?: unknown): Promise<T> {
        return (await this._client.post<T>(this._buildEndpoint(endpoint), data)).data;
    }

    /**
     * Makes a PUT request to the specified endpoint
     * @param endpoint - The API endpoint to call
     * @param data - The request payload
     * @returns Promise resolving to the response data
     */
    async put<T>(endpoint: string, data?: unknown): Promise<T> {
        return (await this._client.put<T>(this._buildEndpoint(endpoint), data)).data;
    }

    /**
     * Makes a DELETE request to the specified endpoint
     * @param endpoint - The API endpoint to call
     * @returns Promise resolving to the response data
     */
    async delete<T>(endpoint: string): Promise<T> {
        return (await this._client.delete<T>(this._buildEndpoint(endpoint))).data;
    }
}
